import { useQuery } from '@tanstack/react-query';
import { formatMoney, moneyFromCents } from '@wodi/domain';
import { SearchPicker, type SearchPickerStatus } from '@wodi/ui';
import { useState } from 'react';

import { searchTheatreItems } from '../api/theatre.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from './theatre.module.css';
import { errorText } from './words.ts';

// Type-to-search for a procedure to book or a theatre service used (DESIGN.md §7: no long
// dropdowns for clinical choices), priced for the case's visit payer.

export interface PickedItem {
  serviceItemId: string;
  code: string;
  name: string;
  price: { state: 'priced'; unitPriceCents: string } | { state: 'not_priced'; message: string };
}

export function priceWords(price: PickedItem['price']): string {
  return price.state === 'priced'
    ? formatMoney(moneyFromCents(BigInt(price.unitPriceCents)))
    : 'Not priced';
}

export function TheatreItemPicker({
  label,
  visitId,
  purpose,
  onSelect,
  autoFocus = false,
}: {
  label: string;
  visitId: string;
  purpose: 'procedure' | 'service';
  onSelect: (item: PickedItem) => void;
  autoFocus?: boolean;
}) {
  const online = useOnline();
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const results = useQuery({
    queryKey: ['theatre', 'search', purpose, visitId, trimmed],
    queryFn: () => searchTheatreItems(visitId, purpose, trimmed),
    enabled: online && trimmed.length >= 2,
    staleTime: 30_000,
    retry: false,
  });
  let status: SearchPickerStatus;
  if (trimmed.length < 2) status = 'idle';
  else if (!online) status = 'offline';
  else if (results.isFetching || results.isPending) status = 'searching';
  else if (results.isError) status = 'error';
  else status = 'ready';
  return (
    <SearchPicker
      label={label}
      inputValue={text}
      onInputChange={setText}
      status={status}
      items={results.data?.items ?? []}
      itemsQuery={trimmed}
      getKey={(i) => i.serviceItemId}
      getTextValue={(i) => `${i.code} ${i.name}`}
      renderItem={(i) => (
        <span className={styles.itemMain}>
          <span>
            <span className={`wodi-id ${styles.code}`}>{i.code}</span>
            {i.name}
          </span>
          <span className={styles.itemMeta}>{priceWords(i.price)}</span>
        </span>
      )}
      onSelect={(item) => {
        onSelect(item);
        setText('');
      }}
      isExactMatch={(i, q) => i.code.toLowerCase() === q.trim().toLowerCase()}
      emptyMessage="Nothing matches. Check the spelling or search by code."
      errorMessage={errorText(results.error, 'Search did not work. Try again.')}
      offlineMessage="Searching needs a connection to Wodi."
      autoFocus={autoFocus}
    />
  );
}
